import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type TableHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

function joinClasses(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(' ');
}

export type ButtonVariant = 'standard' | 'primary' | 'danger' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  /**
   * A pending action. The button disables itself while `loading` is true, so a
   * deliberate command cannot be submitted twice, and reports `aria-busy` to
   * assistive technology. Colour alone never carries this — the label changes
   * and a glyph precedes it.
   */
  readonly loading?: boolean;
  /** Text shown in place of the label while `loading`. Falls back to the label. */
  readonly loadingLabel?: ReactNode;
}

export function Button({
  variant = 'standard',
  loading = false,
  loadingLabel,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={joinClasses(
        'annwn-button',
        `annwn-button--${variant}`,
        loading && 'annwn-button--loading',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span className="annwn-button__spinner" aria-hidden="true">
          ◇
        </span>
      ) : null}
      {loading && loadingLabel !== undefined ? loadingLabel : children}
    </button>
  );
}

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix' | 'size'
> {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string;
  readonly prefix?: ReactNode;
  readonly multiline?: boolean;
  readonly textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export function TextField({
  label,
  hint,
  error,
  prefix,
  multiline = false,
  textareaProps,
  id: suppliedId,
  className,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `field-${generatedId}`;
  const hintId = `${id}-hint`;
  const describedBy = hint || error ? hintId : undefined;

  return (
    <div className={joinClasses('annwn-field', className)}>
      <label htmlFor={id}>{label}</label>
      <div className="annwn-field__input-row">
        {prefix ? (
          <span className="annwn-field__prefix" aria-hidden="true">
            {prefix}
          </span>
        ) : null}
        {multiline ? (
          <textarea
            id={id}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            {...textareaProps}
          />
        ) : (
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            {...inputProps}
          />
        )}
      </div>
      {hint || error ? (
        <span
          id={hintId}
          className={joinClasses('annwn-field__hint', error && 'annwn-field__hint--error')}
        >
          {error ?? hint}
        </span>
      ) : null}
    </div>
  );
}

export interface WindowProps extends HTMLAttributes<HTMLDivElement> {
  readonly title: string;
  readonly onClose?: () => void;
  readonly closeLabel?: string;
}

export function Window({
  title,
  onClose,
  closeLabel = 'Close',
  children,
  className,
  ...props
}: WindowProps) {
  return (
    <div className={joinClasses('annwn-window', className)} {...props}>
      <span className="annwn-window__corner annwn-window__corner--tl" aria-hidden="true">
        ┌
      </span>
      <span className="annwn-window__corner annwn-window__corner--tr" aria-hidden="true">
        ┐
      </span>
      <span className="annwn-window__corner annwn-window__corner--bl" aria-hidden="true">
        └
      </span>
      <span className="annwn-window__corner annwn-window__corner--br" aria-hidden="true">
        ┘
      </span>
      <div className="annwn-window__title">
        <span className="annwn-window__glyph" aria-hidden="true">
          ┤
        </span>
        <span className="annwn-window__title-text">{title}</span>
        <span className="annwn-window__spacer" aria-hidden="true" />
        <span className="annwn-window__glyph" aria-hidden="true">
          ├
        </span>
        {onClose ? (
          <Button
            variant="ghost"
            className="annwn-window__close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            ×
          </Button>
        ) : null}
      </div>
      <div className="annwn-window__body">{children}</div>
    </div>
  );
}

export type MeterVariant = 'life' | 'stamina' | 'breath' | 'danger';

export interface MeterProps extends HTMLAttributes<HTMLDivElement> {
  readonly label: string;
  readonly value: number;
  readonly max: number;
  /**
   * What the right-hand end of the label reads. Defaults to `value / max`.
   *
   * A `ReactNode` rather than a number because a meter is not obliged to spell
   * its own quantity: Annwn's HUD puts a *word* here ("labouring", "bleeding
   * freely"), which is the whole of how it keeps a bar without acquiring a
   * hit-point display.
   */
  readonly displayValue?: ReactNode;
  readonly segments?: number;
  readonly variant?: MeterVariant;
}

export function Meter({
  label,
  value,
  max,
  displayValue,
  segments = 20,
  variant = 'life',
  className,
  ...props
}: MeterProps) {
  // The meter never becomes an alternative source of truth: a NaN, an
  // Infinity, or an over-max value from a mis-derived view model is clamped to
  // something drawable rather than trusted or thrown.
  const safeMax = Number.isFinite(max) ? Math.max(max, 1) : 1;
  const rawValue = Number.isFinite(value) ? value : 0;
  const safeValue = Math.min(Math.max(rawValue, 0), safeMax);
  const filled = Math.round((safeValue / safeMax) * segments);

  return (
    <div className={joinClasses('annwn-meter', `annwn-meter--${variant}`, className)} {...props}>
      <div className="annwn-meter__label">
        <span>{label}</span>
        <span className="annwn-meter__value">{displayValue ?? `${value} / ${max}`}</span>
      </div>
      <div
        className="annwn-meter__bar"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={safeValue}
      >
        <span className="annwn-meter__fill" aria-hidden="true">
          {'▓'.repeat(filled)}
        </span>
        <span className="annwn-meter__empty" aria-hidden="true">
          {'░'.repeat(Math.max(segments - filled, 0))}
        </span>
      </div>
    </div>
  );
}

export type BodyPartState = 'ok' | 'hurt' | 'severe';

export interface BodyPart {
  readonly id: string;
  readonly label: string;
  readonly glyph: string;
  readonly state: BodyPartState;
  readonly bleeding?: boolean;
  /**
   * Explicit grid placement, one-based, for a plan the component has never seen
   * — the caller derives it from the body's zones and sides so a boar's four
   * legs land in the leg row. Omit both and parts flow in source order (the
   * default six-part humanoid the stories use).
   */
  readonly row?: number;
  readonly column?: number;
}

export interface BodyDiagramProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  readonly parts: readonly BodyPart[];
  readonly selectedPart?: string;
  readonly onSelect?: (part: BodyPart) => void;
}

export function BodyDiagram({
  parts,
  selectedPart,
  onSelect,
  className,
  ...props
}: BodyDiagramProps) {
  // When any part carries an explicit position the diagram is plan-driven, and
  // the default nth-child humanoid layout must get out of the way.
  const positioned = parts.some((part) => part.row !== undefined || part.column !== undefined);
  return (
    <div
      className={joinClasses(
        'annwn-body-diagram',
        positioned && 'annwn-body-diagram--positioned',
        className,
      )}
      {...props}
    >
      {parts.map((part) => (
        <button
          className={joinClasses(
            'annwn-body-part',
            `annwn-body-part--${part.state}`,
            part.bleeding && 'annwn-body-part--bleeding',
            selectedPart === part.id && 'annwn-body-part--selected',
          )}
          data-part={part.id}
          key={part.id}
          type="button"
          style={
            part.row !== undefined || part.column !== undefined
              ? { gridRow: part.row, gridColumn: part.column }
              : undefined
          }
          aria-label={`${part.label}${part.bleeding ? ', bleeding' : ''}`}
          aria-pressed={selectedPart === part.id}
          onClick={() => onSelect?.(part)}
        >
          <span className="annwn-body-part__glyph" aria-hidden="true">
            {part.glyph}
          </span>
          <span className="annwn-body-part__label">{part.label}</span>
        </button>
      ))}
    </div>
  );
}

export type WoundSeverity = 'bruised' | 'light' | 'deep' | 'severe' | 'crippling' | 'mortal';

/**
 * One line of the wound ledger, as words. A data-only view model: the client
 * turns a protocol wound tuple into this — the component never sees a level, a
 * number, or a protocol type, matching the design's "health is prose, never a
 * number" rule.
 */
export interface WoundSummary {
  readonly id: string;
  /** Display name of the part, e.g. `"left forearm"`. */
  readonly part: string;
  /** Severity as a word: `"Deep"`, `"Crippling"`. */
  readonly severity: string;
  /** Kind of wound as a word: `"cut"`, `"bite"`, `"burn"`. */
  readonly kind: string;
  readonly tone?: WoundSeverity;
  /** Treatment or feeling: `"bandaged this morning, aching"`. */
  readonly note?: string;
  readonly bleeding?: boolean;
}

export interface WoundLedgerProps extends HTMLAttributes<HTMLDivElement> {
  readonly wounds: readonly WoundSummary[];
  readonly label?: string;
  /** Shown when there are no wounds. */
  readonly emptyLabel?: ReactNode;
}

export function WoundLedger({
  wounds,
  label = 'Wounds',
  emptyLabel = 'Unhurt.',
  className,
  ...props
}: WoundLedgerProps) {
  return (
    <div className={joinClasses('annwn-ledger', className)} aria-label={label} {...props}>
      {wounds.length === 0 ? (
        <p className="annwn-ledger__empty">{emptyLabel}</p>
      ) : (
        <ul className="annwn-ledger__list">
          {wounds.map((wound) => (
            <li
              className={joinClasses(
                'annwn-ledger__entry',
                wound.tone && `annwn-ledger__entry--${wound.tone}`,
                wound.bleeding && 'annwn-ledger__entry--bleeding',
              )}
              key={wound.id}
            >
              <p className="annwn-ledger__title">
                {wound.severity} {wound.kind}, {wound.part}
                {wound.bleeding ? (
                  <span className="annwn-ledger__bleeding"> — bleeding</span>
                ) : null}
              </p>
              {wound.note ? <p className="annwn-ledger__note">{wound.note}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type LogTone = 'normal' | 'damage' | 'myth' | 'system' | 'speech';

export interface LogEntry {
  readonly id: string;
  readonly timestamp: string;
  readonly text: string;
  readonly tone?: LogTone;
  /**
   * Who this line is about, for the hover-to-highlight hook (M2-06). Pointing at
   * "the wolf tears at Gwion's arm" can pick both of them out on the grid — but
   * only if the line remembers which wolf. The panel does nothing with these
   * itself; it hands them to `onHighlight` and lets the game light the tiles.
   */
  readonly actors?: readonly string[];
}

export interface LogPanelProps extends HTMLAttributes<HTMLDivElement> {
  readonly entries: readonly LogEntry[];
  readonly label?: string;
  /**
   * Called with an entry's `actors` when the pointer is over it, and with `[]`
   * when it leaves — the DOM-overlay-to-renderer hook M2-06 asks for. Pointer
   * only, by design: a keyboard reader is walking the log, not aiming with it.
   */
  readonly onHighlight?: (actors: readonly string[]) => void;
}

export function LogPanel({
  entries,
  label = 'Event log',
  onHighlight,
  className,
  ...props
}: LogPanelProps) {
  const viewport = useRef<HTMLDivElement>(null);
  // The scroll policy is explicit: follow new events only while the reader is
  // already at the bottom. A player who has scrolled up to re-read what killed
  // them is not yanked back down by the next tick's line.
  const following = useRef(true);

  function onScroll(): void {
    const element = viewport.current;
    if (!element) return;
    following.current = element.scrollHeight - element.scrollTop - element.clientHeight < 4;
  }

  useEffect(() => {
    const element = viewport.current;
    if (element && following.current) element.scrollTop = element.scrollHeight;
  }, [entries]);

  return (
    <div
      className={joinClasses('annwn-log', className)}
      role="log"
      aria-label={label}
      aria-live="polite"
      ref={viewport}
      onScroll={onScroll}
      // Leaving the whole panel clears the highlight, so a pointer sliding off
      // the last line does not strand a wolf lit on the grid.
      onMouseLeave={onHighlight ? () => onHighlight([]) : undefined}
      {...props}
    >
      {entries.map((entry) => (
        <p
          className={joinClasses(
            'annwn-log__entry',
            `annwn-log__entry--${entry.tone ?? 'normal'}`,
            onHighlight && entry.actors && entry.actors.length > 0 && 'annwn-log__entry--actors',
          )}
          key={entry.id}
          onMouseEnter={onHighlight ? () => onHighlight(entry.actors ?? []) : undefined}
        >
          <span className="annwn-log__timestamp">[{entry.timestamp}]</span>
          <span>{entry.text}</span>
        </p>
      ))}
    </div>
  );
}

export interface MenuItem {
  readonly id: string;
  readonly key?: string;
  readonly name: string;
  readonly meta?: string;
  /** A choice the player can see but not take right now. */
  readonly disabled?: boolean;
  /** Why it is disabled — shown in place of the metadata, never hover-only. */
  readonly disabledReason?: string;
}

export interface MenuProps extends Omit<HTMLAttributes<HTMLUListElement>, 'onSelect'> {
  readonly items: readonly MenuItem[];
  readonly selectedId?: string;
  readonly onSelect?: (item: MenuItem) => void;
  readonly label?: string;
  /**
   * Allow a single letter key to pick the item that displays it, the way a
   * roguelike inventory does. The menu only listens while it has focus — it
   * never registers a global handler — so the game input owner still decides
   * when a keystroke reaches it.
   */
  readonly singleKeySelect?: boolean;
}

/** Whether a keystroke landed in a field where letters are text, not commands. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

export function Menu({
  items,
  selectedId,
  onSelect,
  label = 'Menu',
  singleKeySelect = false,
  className,
  onKeyDown,
  ...props
}: MenuProps) {
  const initialId = selectedId ?? items.find((item) => !item.disabled)?.id ?? items[0]?.id;

  function selectableFrom(start: number, step: number): MenuItem | undefined {
    for (let index = start; index >= 0 && index < items.length; index += step) {
      const candidate = items[index];
      if (candidate && !candidate.disabled) return candidate;
    }
    return undefined;
  }

  function moveSelection(event: KeyboardEvent<HTMLButtonElement>, item: MenuItem): void {
    const index = items.findIndex((candidate) => candidate.id === item.id);
    let next: MenuItem | undefined;
    if (event.key === 'ArrowDown') next = selectableFrom(index + 1, 1) ?? item;
    else if (event.key === 'ArrowUp') next = selectableFrom(index - 1, -1) ?? item;
    else if (event.key === 'Home') next = selectableFrom(0, 1);
    else if (event.key === 'End') next = selectableFrom(items.length - 1, -1);
    else return;
    event.preventDefault();
    if (next) onSelect?.(next);
  }

  function onListKeyDown(event: KeyboardEvent<HTMLUListElement>): void {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    // Single-key selection: never while the keystroke is text in a field, and
    // never for a key modified into a shortcut.
    if (
      singleKeySelect &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !isEditableTarget(event.target)
    ) {
      const match = items.find((item) => !item.disabled && item.key === event.key);
      if (match) {
        event.preventDefault();
        onSelect?.(match);
      }
    }
  }

  return (
    <ul
      className={joinClasses('annwn-menu', className)}
      role="listbox"
      aria-label={label}
      onKeyDown={onListKeyDown}
      {...props}
    >
      {items.map((item) => {
        const isSelected = (selectedId ?? initialId) === item.id;
        const meta = item.disabled && item.disabledReason ? item.disabledReason : item.meta;
        return (
          <li key={item.id} role="presentation">
            <button
              className={joinClasses(
                'annwn-menu__item',
                isSelected && 'annwn-menu__item--selected',
                item.disabled && 'annwn-menu__item--disabled',
              )}
              type="button"
              role="option"
              aria-selected={isSelected}
              aria-disabled={item.disabled || undefined}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => {
                if (!item.disabled) onSelect?.(item);
              }}
              onKeyDown={(event) => {
                if (!item.disabled) moveSelection(event, item);
              }}
            >
              {item.key ? (
                <span className="annwn-menu__key" aria-hidden="true">
                  {item.key}
                </span>
              ) : null}
              <span className="annwn-menu__name">{item.name}</span>
              {meta ? (
                <span
                  className={joinClasses(
                    'annwn-menu__meta',
                    item.disabled && item.disabledReason && 'annwn-menu__meta--reason',
                  )}
                >
                  {meta}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface Tab {
  readonly id: string;
  readonly label: string;
  readonly panel: ReactNode;
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  readonly tabs: readonly Tab[];
  readonly defaultTab?: string;
  readonly onChange?: (id: string) => void;
  /**
   * `automatic` (the ARIA default) selects a tab the moment arrow keys move
   * focus onto it; `manual` moves focus without selecting until Enter/Space.
   * Manual is the honest choice when swapping a panel is expensive.
   */
  readonly activation?: 'automatic' | 'manual';
  readonly label?: string;
}

export function Tabs({
  tabs,
  defaultTab,
  onChange,
  activation = 'automatic',
  label = 'Sections',
  className,
  ...props
}: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab ?? tabs[0]?.id ?? '');
  const active = tabs.some((tab) => tab.id === activeTab) ? activeTab : (tabs[0]?.id ?? '');
  const tabListId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  function selectTab(id: string): void {
    setActiveTab(id);
    onChange?.(id);
  }

  function focusTabAt(index: number): void {
    const wrapped = (index + tabs.length) % tabs.length;
    const tab = tabs[wrapped];
    if (!tab) return;
    const button = listRef.current?.querySelector<HTMLButtonElement>(
      `#${CSS.escape(`${tabListId}-${tab.id}`)}`,
    );
    button?.focus();
    if (activation === 'automatic') selectTab(tab.id);
  }

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (event.key === 'ArrowRight') focusTabAt(index + 1);
    else if (event.key === 'ArrowLeft') focusTabAt(index - 1);
    else if (event.key === 'Home') focusTabAt(0);
    else if (event.key === 'End') focusTabAt(tabs.length - 1);
    else return;
    event.preventDefault();
  }

  return (
    <div className={joinClasses('annwn-tabs', className)} {...props}>
      <div className="annwn-tabs__list" role="tablist" aria-label={label} ref={listRef}>
        {tabs.map((tab, index) => (
          <button
            className={joinClasses(
              'annwn-tabs__tab',
              active === tab.id && 'annwn-tabs__tab--active',
            )}
            id={`${tabListId}-${tab.id}`}
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            aria-controls={`${tabListId}-${tab.id}-panel`}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => onTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab) => (
        <div
          className={joinClasses(
            'annwn-tabs__panel',
            active === tab.id && 'annwn-tabs__panel--active',
          )}
          id={`${tabListId}-${tab.id}-panel`}
          key={tab.id}
          role="tabpanel"
          aria-labelledby={`${tabListId}-${tab.id}`}
          hidden={active !== tab.id}
          tabIndex={0}
        >
          {tab.panel}
        </div>
      ))}
    </div>
  );
}

/**
 * The tones a chip may take. Each is a *meaning*, never a decoration: `life`
 * is the good news (a prime herb, a guard that is up), `warn` is the thing you
 * should do something about before it gets worse, `cold` is weather and
 * breath, `bleed` is harm, and `myth` is the otherworld leaning in. RC2-07
 * added `life` and `warn` because the HUD had three kinds of status to tell
 * apart and two tones to do it with.
 */
export type BadgeTone = 'neutral' | 'life' | 'warn' | 'bleed' | 'cold' | 'myth';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: BadgeTone;
  readonly children: ReactNode;
}

export function Badge({ tone = 'neutral', className, children, ...props }: BadgeProps) {
  return (
    <span className={joinClasses('annwn-badge', `annwn-badge--${tone}`, className)} {...props}>
      <span className="annwn-badge__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * `StatusTag` is `Badge` in the status vocabulary's clothes: the same props, the
 * same tones, and the same rule that the tone is a dot *and* the label text,
 * never colour alone — a chip that only turned red would be a warning a
 * colour-blind player could not read.
 *
 * What differs is the case, and RC2-07 separated them because the approved HUD
 * reference does: a `Badge` is a **stamp** on a thing ("PRIME") and reads in
 * small caps, while a `StatusTag` is a **sentence about you** ("Hungry",
 * "Guard up") and reads as one. Six shouted words along the top of the screen
 * is a row nobody can skim.
 */
export function StatusTag({ className, ...props }: StatusTagProps) {
  return <Badge className={joinClasses('annwn-status-tag', className)} {...props} />;
}

export type StatusTagTone = BadgeTone;
export type StatusTagProps = BadgeProps;

export interface TooltipProps {
  readonly content: ReactNode;
  readonly children: ReactNode;
  /** Pointer hover delay before the tip appears, in ms. Focus shows it at once. */
  readonly delay?: number;
}

/** The breathing room the tip keeps from its trigger and from the viewport edge. */
const TOOLTIP_GAP = 8;
const TOOLTIP_MARGIN = 4;

/**
 * Where the open tip sits, in viewport coordinates.
 *
 * Computed rather than left to CSS because a CSS-only tip can only ever open in
 * one direction, and the one direction it opened in — up and centred — put its
 * text off the top-left of the screen whenever the trigger was near an edge
 * (which, in a centred Storybook canvas, it always was). Fixed coordinates let
 * the tip flip below when there is no room above and clamp sideways so it is
 * never pushed off-screen — the same edge that bites in the game client.
 */
function tooltipCoords(
  trigger: HTMLElement,
  tip: HTMLElement,
): { readonly top: number; readonly left: number } {
  const t = trigger.getBoundingClientRect();
  const c = tip.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Prefer above; flip below when the tip would clear the top of the viewport.
  const above = t.top - c.height - TOOLTIP_GAP;
  const below = t.bottom + TOOLTIP_GAP;
  const top =
    above >= TOOLTIP_MARGIN || below + c.height > vh - TOOLTIP_MARGIN
      ? Math.max(TOOLTIP_MARGIN, above)
      : below;

  // Centre on the trigger, then clamp so neither edge leaves the viewport.
  const centred = t.left + t.width / 2 - c.width / 2;
  const left = Math.min(
    Math.max(TOOLTIP_MARGIN, centred),
    Math.max(TOOLTIP_MARGIN, vw - c.width - TOOLTIP_MARGIN),
  );

  return { top, left };
}

/**
 * A supplementary hint only. A tooltip is `role="tooltip"` and never the sole
 * home of an essential explanation — an action's disabled reason lives in the
 * menu, not here. It opens on hover (after a short delay) or focus and is
 * dismissable with Escape, so a keyboard reader is never trapped by it.
 */
export function Tooltip({ content, children, delay = 400 }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ readonly top: number; readonly left: number } | null>(
    null,
  );
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  function showAfterDelay(): void {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delay);
  }

  function hide(): void {
    clearTimeout(timer.current);
    setOpen(false);
  }

  const reposition = useCallback(() => {
    if (triggerRef.current && tipRef.current) {
      setCoords(tooltipCoords(triggerRef.current, tipRef.current));
    }
  }, []);

  // Place the tip the frame it opens, before paint, so it never flashes at the
  // wrong edge; keep it anchored while it is open if the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <span
      className="annwn-tooltip"
      onMouseEnter={showAfterDelay}
      onMouseLeave={hide}
      onFocus={() => setOpen(true)}
      onBlur={hide}
      onKeyDown={(event) => {
        if (event.key === 'Escape') hide();
      }}
    >
      <span
        ref={triggerRef}
        className="annwn-tooltip__trigger"
        tabIndex={0}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>
      <span
        ref={tipRef}
        className={joinClasses('annwn-tooltip__content', open && 'annwn-tooltip__content--open')}
        id={id}
        role="tooltip"
        style={
          open && coords
            ? {
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                bottom: 'auto',
                transform: 'none',
                zIndex: 1000,
              }
            : undefined
        }
      >
        {content}
      </span>
    </span>
  );
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** A programmatic description, associated with the dialog for screen readers. */
  readonly description?: ReactNode;
  /**
   * Whether Escape and a backdrop click dismiss the dialog. `ConfirmDialog`
   * sets this to a cancel action; a genuinely blocking dialog can turn it off.
   */
  readonly dismissable?: boolean;
}

/**
 * A modal window that owns focus. Opening moves focus inside and traps Tab;
 * closing restores focus to whatever opened it. It decides nothing about game
 * rules — it only reports that the player closed it.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  description,
  dismissable = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    const initial = dialog?.querySelector<HTMLElement>('[data-autofocus]') ?? dialog;
    initial?.focus();

    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape' && dismissable) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableWithin(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const activeElement = document.activeElement;
      if (event.shiftKey && (activeElement === first || activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [dismissable, onClose, open]);

  if (!open) return null;

  function closeOnBackdrop(event: MouseEvent<HTMLDivElement>): void {
    if (dismissable && event.target === event.currentTarget) onClose();
  }

  return (
    <div className="annwn-popup" onMouseDown={closeOnBackdrop}>
      <div
        className="annwn-popup__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <Window title={title} onClose={dismissable ? onClose : undefined}>
          {description ? (
            <p className="annwn-dialog__description" id={descriptionId}>
              {description}
            </p>
          ) : null}
          {children}
        </Window>
      </div>
    </div>
  );
}

/** `Popup` is the original name for `Dialog`; kept for existing call sites. */
export type PopupProps = DialogProps;
export const Popup = Dialog;

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /**
   * A destructive, irreversible choice. The confirm button turns blood-red and
   * carries a warning glyph — colour is never the sole signal — and the whole
   * point of the component holds: confirmation happens only on the explicit
   * confirm button, never from Escape or a backdrop click.
   */
  readonly destructive?: boolean;
}

/**
 * A confirmation built on `Dialog`. Escape and the backdrop *cancel* — they
 * never confirm — and focus lands on the cancel button, so the safe choice is
 * the default and a destructive action always takes a deliberate second press.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} title={title} onClose={onCancel} description={message} dismissable>
      <div className="annwn-confirm__actions">
        <Button variant="ghost" data-autofocus onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
          {destructive ? '⚠ ' : ''}
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- *
 * Table
 * -------------------------------------------------------------------------- */

export interface TableColumn<Row> {
  /** Stable identity for the column, and its React key. */
  readonly key: string;
  readonly header: ReactNode;
  /** How to draw this column's cell for a row. Returns any node. */
  readonly cell: (row: Row) => ReactNode;
  /** `end` right-aligns the column — for weights, counts, and other numerals. */
  readonly align?: 'start' | 'end';
  /** A width hint, e.g. `'6rem'` or `'40%'`. */
  readonly width?: string;
}

export interface TableProps<Row> extends Omit<TableHTMLAttributes<HTMLTableElement>, 'onSelect'> {
  readonly columns: readonly TableColumn<Row>[];
  readonly rows: readonly Row[];
  /** A stable key per row — its React key and the value `selectedKey` matches. */
  readonly rowKey: (row: Row) => string;
  readonly caption?: ReactNode;
  /** Shown in place of the body when there are no rows. */
  readonly empty?: ReactNode;
  /**
   * Makes rows activatable — a click or Enter/Space picks the row. Left unset,
   * the table is a plain read-only readout. When set, a row is a real focus
   * stop, so a keyboard reader can walk the satchel and open any line.
   */
  readonly onRowSelect?: (row: Row) => void;
  /** The picked row's key, highlighted. Pairs with `onRowSelect`. */
  readonly selectedKey?: string;
}

/**
 * A semantic data table — the readout the satchel, the barter stall and any
 * other "here is a list of things with columns" surface is drawn from.
 *
 * Presentational by default; pass `onRowSelect` to make each row a focus stop
 * that opens something (a description `Popup`, say). It never decides what a row
 * *means* — it draws the cells it is handed and reports which row was chosen,
 * the same contract the rest of the library keeps with the game.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  empty = 'Nothing here.',
  onRowSelect,
  selectedKey,
  className,
  ...props
}: TableProps<Row>) {
  const selectable = onRowSelect !== undefined;

  const cellClass = (column: TableColumn<Row>): string =>
    joinClasses('annwn-table__cell', column.align === 'end' && 'annwn-table__cell--end');

  return (
    <table className={joinClasses('annwn-table', className)} {...props}>
      {caption ? <caption className="annwn-table__caption">{caption}</caption> : null}
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={joinClasses(
                'annwn-table__cell',
                'annwn-table__head',
                column.align === 'end' && 'annwn-table__cell--end',
              )}
              style={column.width ? { width: column.width } : undefined}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="annwn-table__empty" colSpan={columns.length}>
              {empty}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const key = rowKey(row);
            const isSelected = selectedKey !== undefined && key === selectedKey;
            return (
              <tr
                key={key}
                className={joinClasses(
                  selectable && 'annwn-table__row--selectable',
                  isSelected && 'annwn-table__row--selected',
                )}
                {...(selectable
                  ? {
                      tabIndex: 0,
                      onClick: () => onRowSelect(row),
                      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowSelect(row);
                        }
                      },
                    }
                  : {})}
              >
                {columns.map((column) => (
                  <td key={column.key} className={cellClass(column)}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}
